/** The public home page, shown at `/` to anyone who isn't signed in. */
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { planPrice } from '../lib/billing';
import { cx } from '../components/ui';
import { Art, ArtIcon, type ArtName, type IconName } from './art';
import { Band, ClosingCta, Eyebrow, FaqList, Panel, SiteButton, SiteLayout } from './chrome';
import { homeFaq, PAGES } from './content';
import { siteOrigin, useFacts, usePublicPlan } from './hooks';
import { ASSISTANT_TABS, AssistantMock, CommandCenterMock, type AssistantTab } from './mocks';

export function LandingPage() {
  return (
    <SiteLayout title={PAGES['/'].title}>
      <Hero />
      <BuiltIn />
      <Product />
      <Pillars />
      <Assistant />
      <Figures />
      <Developers />
      <PricingTeaser />
      <Questions />
      <ClosingCta />
    </SiteLayout>
  );
}

/**
 * The green panel: the 3D scene fills its top, under the floating nav, and fades into the panel's green, where the
 * headline sits bottom left and the way in bottom right. Phones and portrait tablets get the square picture, with the text below
 * the board (see `NARROW` in art.tsx).
 */
function Hero() {
  const trialDays = usePublicPlan().data?.trialDays ?? 14;
  return (
    <Panel className="bg-site-green text-site-white" outer="-mt-[70px] pt-2 sm:pt-3">
      <Art
        name="hero"
        priority
        controls="nav"
        sizes="(min-width: 1600px) 1600px, 100vw"
        className="site-fade-b"
        alt="A split-flap board on a lime podium reads Cash, Revenue and Accruals, each with a tick, and Books Closed. Around it on coloured plinths stand blue month cubes, a robot arm stamping sheets on a conveyor, a console of switches, a receipt printer with coins, a keyboard and monitor, and a pile of ledgers, in a meadow of grass and small flowers. Rolling hills fade into a blue sky with clouds, and coins and ticked sheets drift overhead."
      />
      <div className="relative z-[2] mx-auto -mt-[32vw] grid max-w-[1400px] gap-7 px-6 pb-[128px] sm:px-10 sm:pb-[140px] sm:landscape:-mt-[15vw] lg:-mt-[15vw] lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-end lg:gap-16 lg:px-14 min-[1600px]:-mt-[240px]">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[13px] text-site-white/85 ring-1 ring-white/15">
            <span className="site-swatch size-2.5 [--m:var(--color-site-lime)]" />
            Continuous close for SaaS finance teams
          </div>
          <h1 className="site-h1 mt-6 max-w-[14ch]">
            Month-end is a review, <span className="text-site-lime">not a scramble.</span>
          </h1>
        </div>
        <div className="lg:pb-2">
          <p className="site-lead max-w-[48ch] text-site-white/75">
            RareMargin is a general ledger where revenue, amortization and accruals post the day they happen. AI drafts the entries you would write by hand, and nothing reaches the books until a person
            approves it.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <SiteButton to="/signup" variant="lime" size="lg" arrow>
              Start free trial
            </SiteButton>
            <SiteButton to="/pricing" variant="white" size="lg">
              See pricing
            </SiteButton>
          </div>
          <div className="site-eyebrow mt-5 leading-normal text-balance text-site-white/55">{trialDays}-day trial · no card · sample company included</div>
        </div>
      </div>
    </Panel>
  );
}

const BUILT_IN: { icon: IconName; title: string; text: string }[] = [
  { icon: 'revenue', title: 'ASC 606 revenue schedules', text: 'Each contract becomes a schedule that posts its revenue on time.' },
  { icon: 'balance', title: 'Double entry, always balanced', text: 'An entry that does not balance never reaches the ledger.' },
  { icon: 'chain', title: 'SHA-256 hash chain', text: 'Every posting is chained to the one before, so an edit cannot hide.' },
  { icon: 'approval', title: 'Approvals and segregation of duties', text: 'Rules route entries to a reviewer, and nobody approves their own.' },
  { icon: 'entities', title: 'Multi-entity and FX', text: 'Subsidiaries, currencies and exchange rates in one set of books.' },
  { icon: 'api', title: 'REST API and MCP', text: 'A versioned API, webhooks and an MCP server for AI agents.' },
];

/**
 * Where a logo strip would go: no customer logos yet, so what the ledger is built on, on a light card that overlaps the
 * hero. Each has a small desk object doing its job on a loop, ruled off from the next like the cells of a ledger.
 */
function BuiltIn() {
  return (
    <section aria-labelledby="built-in" className="relative z-[2] mx-auto -mt-[92px] max-w-[1232px] px-4 sm:-mt-[100px] sm:px-8">
      <div className="overflow-hidden rounded-[24px] border border-site-rule/70 bg-site-white shadow-[0_24px_60px_-34px_rgb(27_26_24/0.45)] sm:rounded-[32px]">
        <h2 id="built-in" className="px-6 pt-7 pb-5 text-[22px] leading-[1.15] font-[580] tracking-[-0.03em] text-balance text-site-ink sm:px-9 sm:pt-9 sm:pb-7 sm:text-[26px] lg:px-11">
          Built into the ledger, <span className="text-site-green">not bolted on.</span>
        </h2>
        <ul className="grid gap-px border-t border-site-rule bg-site-rule sm:grid-cols-2 lg:grid-cols-3">
          {BUILT_IN.map((f) => (
            <li key={f.title} className="flex items-start gap-2 bg-site-white py-5 pr-6 pl-2.5 sm:py-6 sm:pr-8 sm:pl-4 lg:pl-6">
              {/* The pictures have room around the plinth for the shadow, so they reach into the padding. */}
              <ArtIcon name={f.icon} className="-my-3 size-[76px] shrink-0 sm:size-[88px]" />
              <div className="pt-1.5 sm:pt-2.5">
                <h3 className="text-[15.5px] leading-snug font-[580] tracking-[-0.01em] text-site-ink">{f.title}</h3>
                <p className="mt-1 text-[14px] leading-relaxed text-pretty text-site-ink-2">{f.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** The product itself, right after the art: the command center of the sample company, on an oat panel. */
function Product() {
  return (
    <Band inner="!pt-14 !pb-6 lg:!pt-20 lg:!pb-10">
      <div className="text-center">
        <Eyebrow className="justify-center">The command center</Eyebrow>
        <h2 className="site-h2 mx-auto mt-5 max-w-[20ch]">
          Everything waiting on a person, <span className="text-site-green">on one screen.</span>
        </h2>
        <p className="site-lead mx-auto mt-6 max-w-[56ch] text-site-ink-2">Entries to approve, matches to confirm and accruals the assistant proposed, beside cash, ARR, runway and the controls that check the books.</p>
      </div>
      <div className="mt-12 rounded-[28px] bg-site-oat p-2 sm:rounded-[40px] sm:p-4 lg:mt-14 lg:p-6">
        <div className="overflow-hidden rounded-[20px] shadow-[0_30px_60px_-40px_rgb(27_26_24/0.45)] ring-1 ring-black/5 sm:rounded-[26px]">
          <CommandCenterMock />
        </div>
      </div>
    </Band>
  );
}

interface Pillar {
  id: string;
  n: string;
  label: string;
  /** Panel tint, chip, small-text ink and the large accent, all from Clay's palette. */
  tint: string;
  chip: string;
  ink: string;
  accent: string;
  button: { variant: 'accent' | 'lime'; color?: string };
  lead: string;
  then: string;
  points: [string, string][];
  art: ArtName;
  alt: string;
  record: { label: string; title: string; value: string };
}

const PILLARS: Pillar[] = [
  {
    id: 'revenue',
    n: '01',
    label: 'Revenue',
    tint: 'bg-site-revenue',
    chip: 'bg-site-revenue-2 text-site-revenue-ink',
    ink: 'text-site-revenue-ink',
    accent: 'text-site-revenue-accent',
    button: { variant: 'accent', color: 'var(--color-site-revenue-accent)' },
    lead: 'Contracts in.',
    then: 'Recognized revenue out.',
    points: [
      ['Schedules from contracts.', 'ASC 606 schedules post revenue as each service period arrives; deferred revenue rolls forward on its own.'],
      ['Invoices and collections.', 'AR aging by customer, and bank receipts matched to invoices.'],
      ['SaaS metrics from the ledger.', 'MRR waterfall, ARR, NRR and GRR, from the same contracts that drive revenue.'],
    ],
    art: 'revenue',
    alt: 'A tray of two-tone blue cubes, one for each month from J to D, turning over one after another as each month is recognized.',
    record: { label: 'JE-2304 · posted by the scheduler', title: 'October revenue, CON-118', value: '$10,000.00' },
  },
  {
    id: 'spend',
    n: '02',
    label: 'Spend',
    tint: 'bg-site-spend',
    chip: 'bg-site-spend-2 text-site-spend-ink',
    ink: 'text-site-spend-ink',
    accent: 'text-site-spend-accent',
    button: { variant: 'accent', color: 'var(--color-site-spend-accent)' },
    lead: 'Bills in.',
    then: 'Accruals before you ask.',
    points: [
      ['Bills and vendors.', 'AP aging by vendor, and spend by vendor, department or account.'],
      ['Accruals, suggested.', 'When a regular vendor’s bill hasn’t arrived, the accrual is suggested for you, and it reverses itself next month.'],
      ['Prepaids that amortize.', 'Amortization posts monthly from the schedule, not from a spreadsheet.'],
    ],
    art: 'spend',
    alt: 'An invoice slides under a scanner’s light on a peach bed; its coloured lines lift off one by one and drop onto stacks in sorting trays.',
    record: { label: 'Proposed accrual · needs a controller', title: 'AWS, October usage', value: '$18,400.00' },
  },
  {
    id: 'close',
    n: '03',
    label: 'Cash and close',
    tint: 'bg-site-close',
    chip: 'bg-site-close-2 text-site-close-ink',
    ink: 'text-site-close-ink',
    accent: 'text-site-close-accent',
    button: { variant: 'lime' },
    lead: 'Cash matched.',
    then: 'Controls always on.',
    points: [
      ['Bank matching you can explain.', 'Every suggestion shows its score and the factors behind it.'],
      ['Controls that never sleep.', 'Trial balance, tie-outs, closed periods, suspense and the hash chain, checked continuously.'],
      ['A close that is a checklist.', 'By month-end the postings are done; what is left is review and sign-off.'],
    ],
    art: 'close',
    alt: 'A console of toggle switches: each is flipped on in turn and its lamp lights, then a press of the big button lights the dome.',
    record: { label: 'Bank match · score 92', title: 'ACH credit to INV-1042', value: '+$24,000.00' },
  },
];

/** Three tinted cards that stack as you scroll, on large screens each a little lower than the last. */
function Pillars() {
  const stack = useStickyStack();
  return (
    <Band id="product" inner="!pt-16 lg:!pt-24">
      <div className="max-w-[780px]">
        <Eyebrow>The ledger</Eyebrow>
        <h2 className="site-h2 mt-5">
          Every subledger posts to <span className="text-site-green">one ledger.</span>
        </h2>
        <p className="site-lead mt-6 max-w-[56ch] text-site-ink-2">Billing, spend and cash don’t sync into the books overnight. They are the books, so every report reads the same posted entries.</p>
      </div>
      {/* A gap, not space-y: a sticky card's margin counts as part of it, and Tailwind's space-y puts one under each card. */}
      <div ref={stack} className="mt-14 flex flex-col gap-4 lg:gap-8">
        {PILLARS.map((p, i) => (
          <article
            key={p.id}
            id={p.id}
            style={{ '--i': i } as CSSProperties}
            className={cx(
              'grid scroll-mt-24 items-center gap-8 rounded-[28px] p-3 shadow-[0_-24px_48px_-36px_rgb(27_26_24/0.35)] sm:rounded-[40px] sm:p-5 lg:grid-cols-2 lg:gap-12 lg:p-6',
              // Until useStickyStack has measured: no sticking on small screens, the 16px steps on large ones.
              'sticky top-[var(--stick,auto)] lg:top-[var(--stick,calc(88px+var(--i)*16px))]',
              p.tint,
            )}
          >
            <figure className={cx('relative m-0', i % 2 === 1 && 'lg:order-2')}>
              <div className="overflow-hidden rounded-[20px] sm:rounded-[28px]">
                <Art name={p.art} alt={p.alt} controls="top" sizes="(min-width: 1024px) 600px, 100vw" />
              </div>
              <Record {...p.record} tone={p.ink} className={i % 2 === 1 ? 'sm:right-6' : 'sm:left-6'} />
            </figure>
            <div className={cx('px-3 pb-6 sm:px-5 lg:py-8', i % 2 === 1 ? 'lg:order-1 lg:pl-10' : 'lg:pr-10')}>
              <span className={cx('site-eyebrow inline-flex items-center gap-2 rounded-full px-3 py-1.5', p.chip)}>
                {p.n} <span className="opacity-50">/</span> {p.label}
              </span>
              <h3 className="mt-6 font-display text-[clamp(30px,3.4vw,46px)] leading-[1.02] font-[580] tracking-[-0.04em] text-balance">
                {p.lead}
                <br />
                <span className={p.accent}>{p.then}</span>
              </h3>
              <ul className="mt-8 space-y-4 border-t border-site-ink/10 pt-6">
                {p.points.map(([b, t]) => (
                  <li key={b} className="text-[15.5px] leading-relaxed text-site-ink-2">
                    <b className="font-[600] text-site-ink">{b}</b> {t}
                  </li>
                ))}
              </ul>
              {/* Full width on phones, where the two buttons don't fit side by side. */}
              <div className="mt-8 grid gap-2.5 sm:flex sm:flex-wrap">
                <SiteButton to="/signup" variant={p.button.variant} arrow style={p.button.color ? ({ '--accent': p.button.color } as CSSProperties) : undefined}>
                  Try it on a sample company
                </SiteButton>
                <SiteButton to="/pricing" variant="white">
                  See pricing
                </SiteButton>
              </div>
            </div>
          </article>
        ))}
      </div>
    </Band>
  );
}

/**
 * Where each stacked card sticks. When the screen shows the tallest card under the nav, each sits 16px below the last,
 * so the tops of the ones behind show, and the cards all take the tallest one's height: the stack lets go when a back
 * card's bottom meets the front card's, so a shorter last card made it leave before that card had landed. On a shorter
 * screen (phones, tablets, small laptops) each card scrolls until its bottom meets the bottom of the screen and waits
 * there while the next slides over it, so the whole card, buttons included, has been in view first. The screen height is
 * the one with the browser's bars showing (`clientHeight`), which stays put while a phone hides and shows them.
 */
function useStickyStack() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const cards = [...(ref.current?.children ?? [])] as HTMLElement[];
    const place = () => {
      cards.forEach((card) => (card.style.minHeight = ''));
      const screen = document.documentElement.clientHeight;
      const heights = cards.map((card) => card.offsetHeight);
      const tallest = Math.max(...heights);
      const top = Math.min(88, screen - tallest);
      // 72px clears the floating nav.
      const fits = top >= 72;
      cards.forEach((card, i) => {
        if (fits) card.style.minHeight = `${tallest}px`;
        card.style.setProperty('--stick', `${fits ? top + i * 16 : screen - heights[i]!}px`);
      });
    };
    // Watches what is inside the cards, not the cards, whose heights this sets.
    const resized = new ResizeObserver(place);
    cards.forEach((card) => [...card.children].forEach((part) => resized.observe(part)));
    window.addEventListener('resize', place);
    return () => {
      resized.disconnect();
      window.removeEventListener('resize', place);
    };
  }, []);
  return ref;
}

/** A slip of the product laid over the art (under it on phones): one real-shaped record from the sample company. */
function Record({ label, title, value, tone, className }: Pillar['record'] & { tone: string; className?: string }) {
  return (
    <div aria-hidden className={cx('relative mt-2 flex items-center justify-between gap-4 rounded-2xl bg-site-white/90 px-4 py-3 shadow-[0_18px_40px_-20px_rgb(27_26_24/0.45)] ring-1 ring-black/5 backdrop-blur-md sm:absolute sm:bottom-6 sm:mt-0 sm:max-w-[calc(100%-3rem)] sm:justify-start', className)}>
      <div className="min-w-0">
        <div className="font-plex text-[11px] leading-snug tracking-[0.04em] text-site-muted uppercase">{label}</div>
        <div className="mt-1 text-[14.5px] leading-snug font-[600] text-site-ink">{title}</div>
      </div>
      <div className={cx('num shrink-0 font-plex text-[15px] font-medium', tone)}>{value}</div>
    </div>
  );
}

function Assistant() {
  const [tab, setTab] = useState<AssistantTab>('ask');
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (i + step + ASSISTANT_TABS.length) % ASSISTANT_TABS.length;
    setTab(ASSISTANT_TABS[next]!.key);
    refs.current[next]?.focus();
  };
  return (
    <Panel id="assistant" className="bg-site-assist">
      <div className="mx-auto max-w-[1264px] px-5 py-[72px] sm:px-8 lg:px-[72px] lg:py-[104px]">
        <div className="grid items-center gap-6 lg:grid-cols-[minmax(0,6fr)_minmax(0,5fr)] lg:gap-10">
          <div>
            <span className="site-eyebrow inline-flex rounded-full bg-site-assist-2 px-3 py-1.5 text-site-assist-ink">The assistant</span>
            <h2 className="site-h2 mt-6">
              AI drafts the entries. <span className="text-site-assist-accent">People approve them.</span>
            </h2>
            <p className="site-lead mt-6 max-w-[48ch] text-site-ink-2">Ask about the books, or have it draft the entry. Whatever it proposes goes to the approval queue, and only a person can post it.</p>
          </div>
          <div className="mx-auto w-full max-w-[480px] overflow-hidden rounded-[24px] sm:rounded-[32px] lg:mr-0">
            <Art
              name="assistant"
              sizes="(min-width: 1024px) 480px, 100vw"
              alt="A robot arm lifts an approval stamp and presses it onto each sheet of paper riding past on a conveyor; stamped sheets carry a tick."
            />
          </div>
        </div>
        <div className="mt-10 grid items-start gap-10 lg:mt-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
          <div role="tablist" aria-orientation="vertical" aria-label="What the assistant does" className="border-t border-site-assist-ink/12">
            {ASSISTANT_TABS.map((t, i) => {
              const on = t.key === tab;
              return (
                <button
                  key={t.key}
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  role="tab"
                  id={`assistant-tab-${t.key}`}
                  aria-selected={on}
                  aria-controls="assistant-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(t.key)}
                  onKeyDown={(e) => onKey(e, i)}
                  className={cx('relative block w-full border-b border-site-assist-ink/12 py-5 pl-6 text-left transition-colors', on ? 'text-site-ink' : 'text-site-ink/55 hover:text-site-ink/85')}
                >
                  <span className={cx('absolute top-5 bottom-5 left-0 w-[3px] rounded-full transition-colors', on ? 'bg-site-assist-accent' : 'bg-transparent')} />
                  <span className="site-eyebrow mr-3 align-middle text-site-assist-ink/50">0{i + 1}</span>
                  <span className="align-middle text-[clamp(20px,1.9vw,24px)] font-[580] tracking-[-0.025em]">{t.title}</span>
                  <span className={cx('grid transition-[grid-template-rows] duration-200', on ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
                    <span className="overflow-hidden">
                      <span className="block max-w-[46ch] pt-2.5 text-[15.5px] leading-relaxed text-site-ink-2">{t.body}</span>
                    </span>
                  </span>
                </button>
              );
            })}
            <p className="mt-6 text-[14px] leading-relaxed text-site-ink-2">Each question counts toward your company’s monthly assistant allowance.</p>
          </div>
          <div id="assistant-panel" role="tabpanel" aria-labelledby={`assistant-tab-${tab}`}>
            <AssistantMock tab={tab} />
          </div>
        </div>
      </div>
    </Panel>
  );
}

/** Plain facts about the product, set like ledger lines on an oat panel. No invented customer numbers. */
function Figures() {
  const plan = usePublicPlan().data;
  const rows: [string, ReactNode][] = [
    ['0', 'entries the assistant or an MCP client can post without a person approving them'],
    ['13', 'reports read straight from the ledger, from the trial balance to a 13-week cash forecast'],
    ['SHA-256', 'seal on every posted entry, chained so a change to any one of them shows'],
    [String(plan?.trialDays ?? 14), 'days to try all of it, on your books or on a sample company with 14 months of history'],
  ];
  return (
    <Panel className="bg-site-oat" outer="mt-2 sm:mt-3">
      <div className="mx-auto grid max-w-[1264px] gap-10 px-5 py-[72px] sm:px-8 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:px-[72px] lg:py-[96px]">
        <div>
          <Eyebrow>By the numbers</Eyebrow>
          <h2 className="mt-5 font-display text-[clamp(30px,3.2vw,44px)] leading-[1.04] font-[580] tracking-[-0.04em] text-balance">The rules are in the ledger, not in a policy doc.</h2>
        </div>
        <dl className="border-t-2 border-site-green">
          {rows.map(([n, label]) => (
            <div key={n} className="grid items-baseline gap-2 border-b border-site-green/20 py-6 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-6">
              <dt className="font-display text-[clamp(38px,5.4vw,76px)] leading-[0.9] font-[600] tracking-[-0.05em] whitespace-nowrap text-site-green">{n}</dt>
              <dd className="text-[16px] leading-relaxed text-site-ink-2">{label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Panel>
  );
}

const mcp = (origin: string) => `claude mcp add --transport http raremargin \\
  ${origin}/mcp \\
  --header "Authorization: Bearer ck_…"`;
const curl = (origin: string) => `curl ${origin}/v1/journal-entries \\
  -H "Authorization: Bearer ck_…" \\
  -H "Idempotency-Key: 8c1f2e" \\
  -d '{
    "date": "2026-10-31",
    "memo": "October payroll",
    "lines": [
      { "account_code": "6000", "debit":  "84250.00" },
      { "account_code": "1000", "credit": "84250.00" }
    ]
  }'`;

function Developers() {
  const origin = siteOrigin();
  return (
    <Band id="developers">
      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-12">
        <div>
          <Eyebrow>Developers</Eyebrow>
          <h2 className="site-h2 mt-5">
            Your books have <span className="text-site-slushie-accent">an API.</span>
          </h2>
          <p className="site-lead mt-6 max-w-[56ch] text-site-ink-2">The app, the REST API, webhooks and the MCP server all run on the same services, so a script gets exactly the rules a person does.</p>
        </div>
        <div className="overflow-hidden rounded-[24px] sm:rounded-[32px]">
          <Art name="developers" sizes="(min-width: 1024px) 460px, 100vw" alt="Keycaps for curly braces, a slash and v1, with a blue RUN key, in front of a monitor that types out lines of code." />
        </div>
      </div>
      <div className="mt-12 grid grid-cols-[minmax(0,1fr)] gap-3 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:grid-rows-2 lg:gap-4">
        <div className="flex flex-col overflow-hidden rounded-[24px] bg-site-ink text-white sm:rounded-[32px] lg:row-span-2">
          <div className="p-7 sm:p-9">
            <Eyebrow className="text-site-lime">REST API · /v1</Eyebrow>
            <h3 className="site-h3 mt-4">Post an entry from anywhere.</h3>
            <p className="mt-3 max-w-[48ch] text-[15.5px] leading-relaxed text-white/65">Decimal-string amounts, idempotent writes, signed cursors and RFC 9457 errors. API keys carry a role, and their writes go through the same approval rules as anyone’s.</p>
          </div>
          <pre className="mt-auto flex-1 overflow-x-auto border-t border-white/10 bg-black/30 p-6 font-plex text-[12.5px] leading-[1.7] text-white/80 sm:px-9">
            <code>{curl(origin)}</code>
          </pre>
        </div>
        <div className="rounded-[24px] bg-site-slushie p-7 sm:rounded-[32px] sm:p-9">
          <Eyebrow className="text-site-slushie-accent">MCP server · /mcp</Eyebrow>
          <h3 className="site-h3 mt-4 text-site-slushie-ink">Bring your own agent.</h3>
          <p className="mt-3 text-[15.5px] leading-relaxed text-site-ink-2">Connect Claude or any MCP client to the same tools the assistant uses. Write tools create approval requests, never postings.</p>
          <pre className="mt-5 overflow-x-auto rounded-xl bg-site-white px-4 py-3 font-plex text-[12px] leading-[1.7] text-site-ink-2 ring-1 ring-site-slushie-2 [scrollbar-width:none]">
            <code>{mcp(origin)}</code>
          </pre>
        </div>
        <div className="rounded-[24px] bg-site-oat p-7 sm:rounded-[32px] sm:p-9">
          <Eyebrow>Webhooks</Eyebrow>
          <h3 className="site-h3 mt-4">Hear about every posting.</h3>
          <p className="mt-3 text-[15.5px] leading-relaxed text-site-ink-2">Signed with HMAC-SHA256, sent from a transactional outbox, retried with backoff, and every delivery logged in Settings.</p>
        </div>
      </div>
    </Band>
  );
}

function PricingTeaser() {
  const data = usePublicPlan().data;
  const price = data?.plan ? planPrice(data.plan) : null;
  const trialDays = data?.trialDays ?? 14;
  return (
    <Band inner="!pt-8 !pb-16 lg:!pt-10 lg:!pb-20">
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div>
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="site-h2 mt-5">
            One plan. <span className="text-site-green">Every feature.</span>
          </h2>
          <p className="site-lead mt-6 max-w-[46ch] text-site-ink-2">No modules to unlock and no add-ons for the assistant or the API. Try it for {trialDays} days, then subscribe when the books are in.</p>
          <Link to="/pricing" className="mt-7 inline-flex items-center gap-1.5 text-[15.5px] font-[540] text-site-green underline-offset-4 hover:underline">
            Pricing and FAQ →
          </Link>
        </div>
        <div className="rounded-[28px] bg-site-oat p-2 sm:rounded-[40px] sm:p-3">
          <div className="rounded-[22px] bg-site-white p-7 shadow-[0_1px_2px_rgb(27_26_24/0.06)] sm:rounded-[32px] sm:p-9">
            <div className="flex items-baseline justify-between gap-4">
              <span className="site-eyebrow text-site-muted">{data?.plan?.name ?? 'RareMargin'}</span>
              <span className="site-eyebrow rounded-full bg-site-lime px-2.5 py-1.5 text-site-lime-ink">{trialDays} days free</span>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              {price ? (
                <>
                  <span className="font-display text-[60px] leading-none font-[580] tracking-[-0.05em]">{price.amount}</span>
                  <span className="text-[16px] text-site-muted">{price.per} per company</span>
                </>
              ) : (
                <span className="font-display text-[34px] leading-none font-[580] tracking-[-0.04em]">Free for {trialDays} days</span>
              )}
            </div>
            <ul className="mt-7 grid gap-2.5 text-[15px] text-site-ink-2 sm:grid-cols-2">
              {['General ledger and close', 'Revenue, AR and AP', 'Bank matching and forecast', 'Controls and hash chain', 'The assistant', 'API, MCP and webhooks'].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Tick /> {t}
                </li>
              ))}
            </ul>
            <SiteButton to="/signup" className="mt-8 w-full" size="lg" arrow>
              Start free trial
            </SiteButton>
          </div>
        </div>
      </div>
    </Band>
  );
}

/** Plain answers to what people ask first, the same text as the page's FAQPage data, which AI assistants can quote. */
function Questions() {
  const facts = useFacts();
  return (
    <Band id="questions" inner="!pt-4 !pb-24 lg:!pt-6 lg:!pb-28">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
        <div>
          <Eyebrow>Questions</Eyebrow>
          <h2 className="site-h2 mt-5 !text-[clamp(30px,3.4vw,44px)]">
            What finance teams <span className="text-site-green">ask first.</span>
          </h2>
          <p className="mt-5 max-w-[36ch] text-[15.5px] leading-relaxed text-site-ink-2">
            More on billing and the trial on the{' '}
            <Link to="/pricing" className="text-site-green underline-offset-4 hover:underline">
              pricing page
            </Link>
            .
          </p>
        </div>
        <FaqList items={homeFaq(facts)} />
      </div>
    </Band>
  );
}

export const Tick = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-site-green" aria-hidden>
    <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeOpacity="0.3" />
    <path d="M5 8.2l2 2 4-4.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
